import React, { useState } from "react";
import Layout from "@/components/Layout";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatCAD } from "@/lib/format";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Checkout() {
  const { cart, subtotal } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: user?.email || "",
    full_name: user?.name || "",
    line1: "",
    line2: "",
    city: "",
    province: "ON",
    postal_code: "",
    phone: "",
    notes: "",
  });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const total = subtotal + subtotal * 0.13 + (subtotal > 75 ? 0 : 9.99);

  const submit = async (e) => {
    e.preventDefault();
    if (!cart?.cart_id || !cart?.lines?.length) {
      toast.error("Cart is empty"); return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/checkout/session", {
        cart_id: cart.cart_id,
        origin_url: origin,
        email: form.email,
        shipping_address: {
          full_name: form.full_name,
          line1: form.line1,
          line2: form.line2 || null,
          city: form.city,
          province: form.province,
          postal_code: form.postal_code,
          country: "Canada",
          phone: form.phone || null,
        },
        notes: form.notes || null,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (!cart?.lines?.length) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <p>Your cart is empty.</p>
          <button onClick={() => nav("/discover")} className="btn-primary mt-4">Browse products</button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="overline mb-2">Checkout</div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-10">Complete your order</h1>

        <form onSubmit={submit} className="grid md:grid-cols-12 gap-8" data-testid="checkout-form">
          <div className="md:col-span-7 space-y-6">
            <Section title="Contact">
              <Field label="Email" required value={form.email} onChange={onChange("email")} type="email" testid="checkout-email" />
            </Section>
            <Section title="Shipping address">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full name" required value={form.full_name} onChange={onChange("full_name")} testid="checkout-name" />
                <Field label="Phone" value={form.phone} onChange={onChange("phone")} />
              </div>
              <Field label="Address line 1" required value={form.line1} onChange={onChange("line1")} testid="checkout-line1" />
              <Field label="Address line 2" value={form.line2} onChange={onChange("line2")} />
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="City" required value={form.city} onChange={onChange("city")} testid="checkout-city" />
                <div>
                  <label className="block text-sm font-medium mb-1.5">Province</label>
                  <select value={form.province} onChange={onChange("province")} className="input-field" data-testid="checkout-province">
                    {["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"].map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <Field label="Postal code" required value={form.postal_code} onChange={onChange("postal_code")} testid="checkout-postal" />
              </div>
            </Section>
            <Section title="Order notes (optional)">
              <textarea className="input-field min-h-[80px]" value={form.notes} onChange={onChange("notes")} placeholder="Gift instructions, delivery notes…" />
            </Section>
          </div>

          <aside className="md:col-span-5">
            <div className="surface p-6 sticky top-24">
              <h3 className="font-heading text-xl mb-4">Your order</h3>
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {cart.lines.map((l, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <img src={l.image} alt="" className="w-14 h-14 object-cover" />
                    <div className="flex-1 text-sm">
                      <div className="line-clamp-1">{l.name}</div>
                      <div className="text-[#8A8782]">× {l.quantity}</div>
                    </div>
                    <div className="font-mono text-sm">{formatCAD(l.unit_price * l.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#E5E3DD] mt-4 pt-4 space-y-1.5 text-sm">
                <Row label="Subtotal" value={formatCAD(subtotal)} />
                <Row label="Tax (HST estimate 13%)" value={formatCAD(subtotal * 0.13)} />
                <Row label="Shipping" value={subtotal > 75 ? "Free" : formatCAD(9.99)} />
                <div className="border-t border-[#E5E3DD] my-2" />
                <div className="flex justify-between text-base font-medium">
                  <span>Total</span><span className="font-mono">{formatCAD(total)}</span>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full mt-5" data-testid="pay-btn">
                {loading ? "Redirecting…" : `Pay ${formatCAD(total)}`}
              </button>
              <p className="text-xs text-[#8A8782] mt-3 text-center">Secure payment via Stripe (test mode)</p>
            </div>
          </aside>
        </form>
      </section>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <div className="surface p-6 space-y-4">
      <h3 className="font-heading text-xl">{title}</h3>
      {children}
    </div>
  );
}
function Field({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}{required && <span className="text-[#702A32]">*</span>}</label>
      <input type={type} value={value} onChange={onChange} required={required} className="input-field" data-testid={testid} />
    </div>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between text-[#5C5A56]"><span>{label}</span><span className="font-mono">{value}</span></div>;
}
